/** Retur penjualan — SATU sumber rumus untuk dua layar: panel detail di
 *  halaman Penjualan (Sales.jsx) dan menu "Ajukan Retur Penjualan" di halaman
 *  Retur (Retur.jsx, brief owner 4 Sep 2026 P18a). Dulu rumus ini hidup di
 *  Sales.jsx saja; kalau disalin ke layar kedua, dua layar bisa menyarankan
 *  angka berbeda untuk nota yang sama — dan tidak ada yang memberi tahu
 *  (jebakan "duplikasi helper melahirkan penyimpangan", CLAUDE.md). */

/** Nominal SARAN pengembalian: dasar baris terpilih x (faktor neto + faktor
 *  PPN), dipagari ke sisa hak retur nota. Dasar baris = harga BERSIH per baris
 *  (`hargaBersih`, nota berdiskon produk — brief 22 Sep D5) atau harga bruto
 *  (nota lama, diskonnya diratakan lewat `faktorNeto`). HANYA memengaruhi
 *  saran — yang dikirim ke backend tetap index_item/qty/alasan per baris;
 *  server yang menghitung ulang dan menjadi validator akhir. */
export function hitungNominalReturDefault(items, faktorNeto, faktorPpn, sisaNilaiMaks) {
  const dasar = items.filter(r => r.checked)
    .reduce((a, r) => a + ((r.hargaBersih ?? r.harga) / (r.qtyAsal || 1)) * (r.qtyRetur || 0), 0);
  const neto = Math.round(dasar * ((faktorNeto ?? 1) + (faktorPpn ?? 0)));
  return Math.max(0, Math.min(neto, sisaNilaiMaks ?? neto));
}

/** Susun baris retur + faktor prorata dari satu nota dan daftar retur atas
 *  nota itu (`GET /retur/penjualan?sale_id`). Retur yang masih aktif
 *  (menunggu approval ATAU sudah disetujui) mengunci qty yang sudah diretur —
 *  dipakai untuk membatasi qty maksimum tiap baris sebelum server menolak
 *  (UX lebih ramah; server tetap validator akhir). */
export function susunReturDariNota(sale, returNota) {
  const aktif = (returNota || []).filter(e => e.status === "menunggu_approval" || e.status === "disetujui");
  const sudahPerIndex = {};
  aktif.forEach(e => (e.items || []).forEach(it => {
    if (it.index_item != null) sudahPerIndex[it.index_item] = (sudahPerIndex[it.index_item] || 0) + (it.qty || 0);
  }));
  // Nota dengan diskon PRODUK per baris (brief 22 Sep D5): potongannya milik
  // baris tertentu, jadi saran retur memakai harga BERSIH baris itu. Meratakan
  // diskon ke semua baris (cara nota lama) menyarankan refund yang terlalu
  // besar untuk barang tanpa diskon dan terlalu kecil untuk barang berdiskon.
  // Nota LAMA (tanpa `items[].diskon`) tetap memakai pembagian rata persis
  // seperti dulu.
  const diskonBaris = (sale.items || []).reduce((a, it) => a + (Number(it.diskon) || 0), 0);
  const diskonPerBaris = diskonBaris > 0;
  const rows = (sale.items || []).map((it, i) => {
    const qtyAsal = it.qty || 1;
    const sudah = sudahPerIndex[i] || 0;
    const sisa = qtyAsal - sudah;
    const harga = it.harga || 0;
    const diskon = Number(it.diskon) || 0;
    return {
      index: i, nama_barang: it.nama_barang, harga,
      diskon, hargaBersih: diskonPerBaris ? Math.max(0, harga - diskon) : harga,
      qtyAsal, sudah, sisa, checked: false, qtyRetur: sisa > 0 ? 1 : 0, alasan: "",
      // Isi paket dibawa supaya baris paket bisa memberi tahu staf APA yang
      // ada di dalamnya SEBELUM ia mencentang. Tanpa ini staf hanya melihat
      // satu baris bernama "Paket Hemat" — satu-satunya baris yang berharga —
      // lalu mengira mencentangnya sudah mengembalikan semuanya. Padahal
      // yang dulu keluar dari gudang adalah ISINYA: meretur baris paket saja
      // mengembalikan UANGNYA tapi barangnya tetap tercatat terjual, unit
      // ber-IMEI tidak bisa dijual lagi, dan persediaan kurang saji permanen.
      // Lihat §41 memory/KEPUTUSAN-TERTUNDA.md.
      komponen: it.komponen || null,
      isBundleComponent: !!it.is_bundle_component,
    };
  });
  const totalHarga = sale.total_harga || 0;
  const diskonNominal = sale.diskon_nominal || 0;
  const kenaPpn = !!sale.kena_ppn;
  const ppnNominalSale = kenaPpn ? (sale.ppn_nominal || 0) : 0;
  // Guard total_harga 0 → faktor 1 (tidak ada dasar untuk prorata, jangan
  // sampai divide-by-zero menghasilkan NaN).
  // Per baris: diskon sudah ada di `hargaBersih` (faktor neto 1), dan PPN
  // nota ini dihitung dari DPP = barang − diskon produk (D7), jadi porsinya
  // dibagi terhadap DPP itu — bukan terhadap total bruto.
  const dppBersih = totalHarga - diskonBaris;
  const faktorNeto = diskonPerBaris ? 1
    : (totalHarga > 0 ? Math.max(0, (totalHarga - diskonNominal) / totalHarga) : 1);
  const faktorPpn = diskonPerBaris
    ? (dppBersih > 0 ? ppnNominalSale / dppBersih : 0)
    : (totalHarga > 0 ? ppnNominalSale / totalHarga : 0);
  const sudahNominalAktif = aktif.reduce((a, e) => a + (e.nominal_kembali || 0), 0);
  // Batas atas = hak retur yang SAMA dengan server (`retur._neto_diterima_sale`):
  // `diskon_nominal` dokumen format baru = jumlah diskon baris.
  const sisaNilaiMaks = Math.max(0, (totalHarga - diskonNominal + ppnNominalSale) - sudahNominalAktif);
  return { rows, faktorNeto, faktorPpn, sisaNilaiMaks, diskonNominal, kenaPpn, ppnNominalSale, diskonPerBaris };
}
